import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaVideo, FaUsers, FaLock, FaGlobe, FaPhone, FaEnvelope, FaWhatsapp } from 'react-icons/fa';

const VelorioOnlineContainer = styled.div`
  color: var(--primary-blue);
  padding-top: 80px;
  min-height: 100vh;
  @media screen and (max-width: 768px) {
    padding-top: 40px;
  }
`;

const VelorioOnlineWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 80px 24px 40px 24px;
`;

const TopLine = styled(motion.p)`
  color: #4CAF50;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  margin-bottom: 16px;
  font-family: 'Open Sans', sans-serif;
`;

const Heading = styled(motion.h1)`
  margin-bottom: 24px;
  font-size: 48px;
  font-weight: 600;
  color: var(--primary-blue);
  font-family: 'Playfair Display', serif;
  @media screen and (max-width: 480px) {
    font-size: 32px;
  }
`;

const Subtitle = styled(motion.p)`
  margin-bottom: 60px;
  font-size: 18px;
  line-height: 24px;
  color: var(--primary-blue);
  font-family: 'Open Sans', sans-serif;
`;

const SectionWrapper = styled.div`
  width: 100vw;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  background: ${({ $even }) => $even ? '#fff' : 'var(--primary-blue)'};
  transition: background 0.3s;
`;

const Section = styled(motion.section)`
  margin-bottom: 0;
  padding: 40px 0;
  max-width: 1200px;
  margin: 0 auto;
  padding: 80px 24px;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 30px;
  color: ${({ $even }) => $even ? 'var(--primary-blue)' : '#fff'};
  font-family: 'Playfair Display', serif;
  @media screen and (max-width: 480px) {
    font-size: 2rem;
  }
`;

const SectionContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: start;
  
  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const SectionText = styled.div`
  color: ${({ $even }) => $even ? 'var(--primary-blue)' : '#fff'};
`;

const SectionDesc = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  color: ${({ $even }) => $even ? 'var(--primary-blue)' : '#fff'};
  font-family: 'Open Sans', sans-serif;
  margin-bottom: 20px;
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 20px 0;
`;

const FeatureItem = styled.li`
  font-size: 1rem;
  line-height: 1.6;
  color: ${({ $even }) => $even ? 'var(--primary-blue)' : '#fff'};
  font-family: 'Open Sans', sans-serif;
  margin-bottom: 10px;
  padding-left: 20px;
  position: relative;
  
  &:before {
    content: '✓';
    position: absolute;
    left: 0;
    color: #4CAF50;
    font-weight: bold;
  }
`;

const IconSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 4rem;
  color: #4CAF50;
  margin-bottom: 20px;
`;

const InfoSection = styled.div`
  background: #f8f9fa;
  border-radius: 15px;
  padding: 30px;
  margin: 30px 0;
  border-left: 5px solid #4CAF50;

  @media screen and (max-width: 768px) {
    padding: 15px;
  }
`;

const InfoTitle = styled.h3`
  font-size: 1.5rem;
  color: var(--primary-blue);
  margin-bottom: 20px;
  font-family: 'Playfair Display', serif;
`;

const InfoText = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #333;
  font-family: 'Open Sans', sans-serif;
  margin-bottom: 15px;
`;

const ContactInfo = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 30px;
  margin: 30px 0;
  border: 2px solid #4CAF50;

  @media screen and (max-width: 768px) {
    padding: 15px;
  }
`;

const ContactTitle = styled.h3`
  font-size: 1.5rem;
  color: ${({ $even }) => $even ? 'var(--primary-blue)' : '#fff'};
  margin-bottom: 20px;
  font-family: 'Playfair Display', serif;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  color: ${({ $even }) => $even ? 'var(--primary-blue)' : '#fff'};
  font-family: 'Open Sans', sans-serif;
`;

const ContactIcon = styled.div`
  margin-right: 15px;
  font-size: 1.2rem;
  color: #4CAF50;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-top: 40px;
`;

const ServiceCard = styled(motion.div)`
  background: ${({ $even }) => $even ? '#fff' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 15px;
  padding: 30px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-top: 5px solid #4CAF50;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
  }
`;

const ServiceIcon = styled.div`
  width: 80px;
  height: 80px;
  background: var(--primary-blue);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: #fff;
  font-size: 2rem;
`;

const ServiceTitle = styled.h3`
  font-size: 1.3rem;
  color: ${({ $even }) => $even ? 'var(--primary-blue)' : '#fff'};
  margin-bottom: 15px;
  font-family: 'Playfair Display', serif;
`;

const ServiceDesc = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: ${({ $even }) => $even ? '#333' : '#fff'};
  font-family: 'Open Sans', sans-serif;
`;

const BtnWrap = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: 20px;
`;

const Button = styled(motion.button)`
  border-radius: 4px;
  background: #4CAF50;
  white-space: nowrap;
  padding: 12px 30px;
  color: #fff;
  font-size: 16px;
  outline: none;
  border: none;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.2s ease-in-out;
  font-family: 'Open Sans', sans-serif;
  font-weight: bold;

  &:hover {
    transition: all 0.2s ease-in-out;
    background: #fff;
    color: #4CAF50;
  }
`;

const VelorioOnline = () => {
  return (
    <VelorioOnlineContainer>
      <VelorioOnlineWrapper>
        <TopLine
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          Velório Online
        </TopLine>
        <Heading
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          Acompanhamento em Tempo Real
        </Heading>
        <Subtitle
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          O Memorial Crematório e Cemitério Dom José disponibiliza o Velório Online para que 
          familiares e amigos possam acompanhar as cerimônias de forma digna e respeitosa, 
          independentemente de onde estejam.
        </Subtitle>
        <iframe src="https://www.adiau.net/validacao/index.php?ipc=814317202&amp;id=81&amp;token=9ec3f0975752a9c1936d2e8841163714&amp;color1=******&amp;color2=******&amp;color3=******&amp;color4=******&amp;color5=******#" width="100%" height="600px" frameborder="0" allowfullscreen="allowfullscreen"> </iframe>
        <br /><br /><br /><br /><br />
        {/* Informações de Acesso */}
        <SectionWrapper $even={false}>
          <Section
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <SectionTitle $even={false}>Como Acessar</SectionTitle>
            <SectionContent>
              <SectionText $even={false}>
                <SectionDesc $even={false}>
                  Para acessar o Terminal de Velório Online é necessário solicitar um usuário e senha 
                  através dos nossos canais de atendimento:
                </SectionDesc>
                
                <ContactInfo $even={false}>
                  <ContactTitle $even={false}>Informações de Contato</ContactTitle>
                  <ContactItem $even={false} onClick={() => window.open('tel:+555535122727')} style={{ cursor: 'pointer' }}>
                    <ContactIcon>
                      <FaPhone />
                    </ContactIcon>
                    <span><strong>Telefone:</strong> (55) 3512 2727</span>
                  </ContactItem>
                  <ContactItem $even={false} onClick={() => {
                    window.open(`https://wa.me/555535122727`, '_blank');
                  }} style={{ cursor: 'pointer' }}>
                    <ContactIcon>
                      <FaWhatsapp />
                    </ContactIcon>
                    <span><strong>WhatsApp:</strong> (55) 3512 2727</span>
                  </ContactItem>
                  <ContactItem $even={false} onClick={() => window.open('mailto:crematorio@crematoriodomjose.com.br')} style={{ cursor: 'pointer' }}>
                    <ContactIcon>
                      <FaEnvelope />
                    </ContactIcon>
                    crematorio@crematoriodomjose.com.br
                  </ContactItem>
                </ContactInfo>

                <BtnWrap>
                  <Button
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 1 }}
                    onClick={() => {
                      const message = encodeURIComponent('Olá, gostaria de solicitar o acesso ao velório online.');
                      window.open(`https://wa.me/555535122727?text=${message}`);
                    }}
                  >
                    Solicitar Acesso
                  </Button>
                </BtnWrap>
              </SectionText>

              <InfoSection>
                <InfoTitle>Segurança e Privacidade</InfoTitle>
                <InfoText>
                  <strong>Canal Privado:</strong> Todos os velórios são transmitidos em canais privados 
                  com acesso restrito apenas para familiares e amigos autorizados.
                </InfoText>
                <InfoText>
                  <strong>Usuário e Senha:</strong> Cada família recebe credenciais únicas e seguras 
                  para acessar a transmissão do velório.
                </InfoText>
                <InfoText>
                  <strong>Autorização Familiar:</strong> A transmissão só é realizada mediante 
                  autorização expressa dos familiares responsáveis.
                </InfoText>
              </InfoSection>
            </SectionContent>
          </Section>
        </SectionWrapper>
        
        {/* Sobre o Velório Online */}
        <SectionWrapper $even={true}>
          <Section
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <SectionTitle $even={true}>Informações Importantes</SectionTitle>
            <SectionContent>
              <SectionText $even={true}>
                <SectionDesc $even={true}>
                  Todos os cerimoniais, desde que autorizados pela família, são exibidos via internet em Canal Privado. 
                  É disponibilizado usuário e senha para o acesso, garantindo assim a sua privacidade.
                </SectionDesc>
                <SectionDesc $even={true}>
                  Sempre em busca do acolhimento de forma digna e respeitosa, buscamos criar alternativas para que 
                  todos os familiares e amigos fiquem próximos neste momento de homenagem e despedida.
                </SectionDesc>
                <SectionDesc $even={true}>
                  Os familiares e amigos acompanham, em tempo real, a despedida de seu ente querido, 
                  onde quer que estejam, proporcionando conforto e proximidade em momentos difíceis.
                </SectionDesc>
              </SectionText>

              <SectionText $even={true}>
                <SectionDesc $even={true}>
                  Para garantir o melhor serviço e experiência para sua família, 
                  observe as seguintes orientações:
                </SectionDesc>
                <FeaturesList $even={true}>
                  <FeatureItem $even={true}>
                    Solicite o acesso com antecedência para garantir a configuração adequada
                  </FeatureItem>
                  <FeatureItem $even={true}>
                    Mantenha suas credenciais de acesso em local seguro
                  </FeatureItem>
                  <FeatureItem $even={true}>
                    Teste a conexão antes do horário da cerimônia
                  </FeatureItem>
                  <FeatureItem $even={true}>
                    Tenha uma conexão estável com a internet
                  </FeatureItem>
                  <FeatureItem $even={true}>
                    Em caso de dúvidas, entre em contato conosco
                  </FeatureItem>
                </FeaturesList>
              </SectionText>
              
            </SectionContent>
          </Section>
        </SectionWrapper>
        
      </VelorioOnlineWrapper>
    </VelorioOnlineContainer>
  );
};

export default VelorioOnline; 